import React, { memo, useContext, useMemo } from 'react';
import { Blok } from '../../../../interfaces/Blok.interface';
import { withBlokWrapper } from '../../../BlokWrapper/BlokWrapper';
import { Theme } from '../../../../interfaces/enums/Theme.enum';
import QuoteStageBlokHeadingBar from '../QuoteStageBlokHeadingBar/QuoteStageBlokHeadingBar';
import { QuoteStageContext } from '../../../../contexts/QuoteStageContext';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { StoryblokNode } from '../../../../interfaces/StoryblokNode.interface';
import { StoryblokPartnerSite } from '../../../../interfaces/StoryblokPartnerSite.interface';
import { StoryblokColourBlokProps } from '../../../../interfaces/StoryblokColourBlokProps';
import { StoryblokColourGradientBlokProps } from '../../../../interfaces/StoryblokColourGradientBlokProps.interface';
import Image from '../../../Image/Image';
import SbImage from '../../../SbImage/SbImage';
import { useSelector } from 'react-redux';
import { getQuoteReference } from '../../../../store/slices/quote/quote.store';
import { getQueryParam } from '../../../../utils/getQueryParam/getQueryParam.util';
import { QuoteContext } from '../../../../contexts/Quote';

interface QuotePartnerSiteBlokProps extends Blok {}

interface QuotePartnerSiteProps {
  blok: QuotePartnerSiteBlokProps;
}

const getQuotePartnerSiteBackgroundColour = (
  partnerSite: StoryblokNode<StoryblokPartnerSite>
): React.CSSProperties => {
  const colourHexes: string[] =
    partnerSite.content?.card_background_colour.flatMap(
      (
        colourBlok: StoryblokColourBlokProps | StoryblokColourGradientBlokProps
      ) => {
        if (colourBlok.component === 'colour') {
          const colourBlokProps: StoryblokColourBlokProps =
            colourBlok as StoryblokColourBlokProps;
          return colourBlokProps.colour.color;
        } else {
          const colourGradientBlokProps: StoryblokColourGradientBlokProps =
            colourBlok as StoryblokColourGradientBlokProps;
          return colourGradientBlokProps.colours.map(
            (colour: StoryblokColourBlokProps) => colour.colour.color
          );
        }
      }
    );

  const backgroundValue: string = colourHexes.length
    ? colourHexes.length > 1
      ? `linear-gradient(${colourHexes.join(', ')})`
      : colourHexes[0]
    : '#fff';
  return { background: backgroundValue };
};

const QuotePartnerSite: React.FC<QuotePartnerSiteProps> = ({ blok }) => {
  const { partner_sites } = useContext(QuoteStageContext);
  const quoteContext = useContext(QuoteContext);

  const quoteReference = quoteContext.quoteDetails?.quoteReference || '';

  const partnerId: string = useMemo(
    () => quoteReference!.slice(0, 7),
    [quoteReference]
  );

  const partnerSite: StoryblokNode<StoryblokPartnerSite> | undefined =
    useMemo(() => {
      return partner_sites.find(
        (partnerSite) => partnerSite.content.api_id === partnerId
      );
    }, [partnerId]);

  if (!partnerSite) {
    return null;
  }

  return (
    <div {...storyblokEditable(blok)}>
      <div className="hidden xl:block">
        <QuoteStageBlokHeadingBar
          heading={
            partnerSite?.content?.heading || 'Our partner conveyancing firm'
          }
        />
      </div>
      {partnerSite && (
        <div
          className="w-full h-[190px] flex items-center justify-center px-10 py-3.5 xl:h-[220px]"
          style={getQuotePartnerSiteBackgroundColour(partnerSite)}
        >
          <SbImage
            asset={partnerSite.content.logo}
            options={{ sizes: { base: 400 } }}
          />
        </div>
      )}
    </div>
  );
};

export default memo(QuotePartnerSite);
